import React from 'react'
import { NavLink, useParams } from 'react-router-dom'
import { Wordmark } from '../ui/Wordmark'
import { AvatarMenu } from '../ui/AvatarMenu'
import { useAuthStore } from '../../store/auth'
import { useChurch } from '../../hooks/useChurches'
import { useSubscriptionStatus } from '../../hooks/useChurchAdmin'
import {
  LayoutDashboard,
  CalendarDays,
  Users,
  CreditCard,
  FileText,
  Building2,
  ChevronDown,
  RefreshCw,
} from 'lucide-react'

interface AdminShellProps {
  children: React.ReactNode
}

const NAV_ITEMS = [
  { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard',      hrefFn: (slug: string) => `/churches/${slug}/dashboard` },
  { id: 'events',    icon: CalendarDays,    label: 'Events',         hrefFn: (slug: string) => `/churches/${slug}/events` },
  { id: 'members',   icon: Users,           label: 'Team',           hrefFn: (slug: string) => `/churches/${slug}/members` },
  { id: 'calendar',  icon: RefreshCw,       label: 'Calendar sync',  hrefFn: (slug: string) => `/churches/${slug}/calendar` },
  { id: 'billing',   icon: CreditCard,      label: 'Billing',        hrefFn: (slug: string) => `/churches/${slug}/billing` },
  { id: 'bulletin',  icon: FileText,        label: 'Bulletin',       hrefFn: (slug: string) => `/churches/${slug}/bulletin` },
  { id: 'profile',   icon: Building2,       label: 'Church profile', hrefFn: (slug: string) => `/churches/${slug}/edit` },
  // { id: 'settings',  icon: Settings,        label: 'Settings',       hrefFn: (slug: string) => `/churches/${slug}/settings` },
]

export function AdminShell({ children }: AdminShellProps) {
  const { slug } = useParams<{ slug: string }>()
  const { user } = useAuthStore()
  const { data: churchData } = useChurch(slug)
  const church = churchData?.data
  const { data: subData } = useSubscriptionStatus(church?.id)
  const sub = subData?.data

  const daysLeft = sub?.currentPeriodEnd
    ? Math.ceil((new Date(sub.currentPeriodEnd).getTime() - Date.now()) / 86_400_000)
    : null

  const isTrialing = sub?.status === 'trialing'
  const isExpiring = !isTrialing && daysLeft !== null && daysLeft <= 14

  return (
    <div className="b-system" style={{ width: '100%', minHeight: '100vh', display: 'flex', background: 'var(--bg)' }}>
      {/* Sidebar */}
      <aside style={{
        width: 232,
        flexShrink: 0,
        padding: '20px 14px 24px',
        background: 'var(--surface)',
        borderRight: '1px solid var(--line-soft)',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        position: 'sticky',
        top: 0,
        height: '100vh',
        overflowY: 'auto',
      }}>
        {/* Logo */}
        <div style={{ padding: '4px 8px 16px' }}>
          <Wordmark size={18} />
        </div>

        {/* Workspace card */}
        {church && (
          <div style={{
            marginBottom: 8,
            padding: '10px 12px',
            borderRadius: 10,
            background: 'linear-gradient(135deg, rgba(59,76,202,.07) 0%, rgba(124,92,211,.10) 100%)',
            border: '1px solid rgba(59,76,202,.12)',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            cursor: 'pointer',
          }}>
            <div style={{
              width: 34,
              height: 34,
              borderRadius: 8,
              background: 'linear-gradient(135deg, #3B4CCA 0%, #7C5CD3 100%)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontSize: 15,
              fontWeight: 700,
              flexShrink: 0,
            }}>
              {church.name.slice(0, 1).toUpperCase()}
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink)', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {church.name}
              </div>
              <div style={{ fontSize: 10.5, color: 'var(--muted)', marginTop: 1 }}>Workspace</div>
            </div>
            <ChevronDown size={13} color="var(--muted)" />
          </div>
        )}

        {/* Nav items */}
        {slug && NAV_ITEMS.map(item => {
          const href = item.hrefFn(slug)
          const Icon = item.icon
          return (
            <NavLink
              key={item.id}
              to={href}
              end={item.id === 'dashboard'}
              style={({ isActive }) => ({
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 10px',
                borderRadius: 8,
                textDecoration: 'none',
                fontSize: 13.5,
                fontWeight: isActive ? 600 : 500,
                color: isActive ? 'var(--primary)' : 'var(--ink-2)',
                background: isActive ? 'var(--primary-soft)' : 'transparent',
              })}
            >
              <Icon size={16} strokeWidth={1.7} />
              <span style={{ flex: 1 }}>{item.label}</span>
            </NavLink>
          )
        })}

        <div style={{ flex: 1 }} />

        {/* Trial / plan status card */}
        {sub && (isTrialing || isExpiring) && (
          <div style={{
            padding: '12px 14px',
            borderRadius: 10,
            background: isTrialing ? '#fef3c7' : '#fee2e2',
            border: `1px solid ${isTrialing ? '#fde68a' : '#fca5a5'}`,
            fontSize: 12,
            color: isTrialing ? '#92400e' : '#b91c1c',
            lineHeight: 1.5,
          }}>
            <div style={{ fontWeight: 700, marginBottom: 3 }}>
              {isTrialing ? `${daysLeft} days left in trial` : `Renews in ${daysLeft} days`}
            </div>
            <div style={{ opacity: 0.85 }}>
              {isTrialing ? 'Subscribe to keep event creation.' : `${sub.plan === 'annual' ? '$9' : '$12'}/mo · ${sub.plan} plan`}
            </div>
          </div>
        )}

        {/* Back to the person-facing app */}
        <NavLink
          to="/"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 10px',
            marginTop: 8,
            borderRadius: 8,
            fontSize: 12.5,
            fontWeight: 600,
            color: 'var(--muted)',
            textDecoration: 'none',
          }}
        >
          ← Back to app
        </NavLink>

        {/* User chip */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 8px 4px', borderTop: '1px solid var(--line-soft)', marginTop: 8 }}>
          <AvatarMenu size={28} activeChurchSlug={slug} direction="up" align="left" />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user?.displayName ?? user?.email}
            </div>
            <div style={{ fontSize: 11, color: 'var(--muted)' }}>My account</div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main style={{ flex: 1, padding: '32px 40px 48px', overflowAuto: 'auto', minWidth: 0 } as React.CSSProperties}>
        {children}
      </main>
    </div>
  )
}
